import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogementsPanel } from "@/components/SettingsPanels";
import { PlanComptableButton } from "@/components/PlanComptableButton";
import { HOTE_FIELDS } from "@/pages/Parametres";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const ETAPES = ["Votre entreprise", "Immeubles et logements", "Plan comptable", "C'est prêt"];

const Etapes = ({ etape }) => (
  <ol className="flex flex-wrap gap-x-6 gap-y-2 lg:flex-col lg:gap-4" data-testid="configuration-etapes">
    {ETAPES.map((nom, i) => (
      <li key={nom} className={`flex items-center gap-3 text-sm ${i === etape ? "font-semibold text-white" : i < etape ? "text-emerald-300" : "text-slate-500"}`} data-testid={`configuration-etape-${i}`}>
        <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full border font-mono text-xs ${i < etape ? "border-emerald-400 bg-emerald-400 text-slate-950" : i === etape ? "border-white" : "border-slate-600"}`}>
          {i < etape ? <Check className="h-3.5 w-3.5" /> : i + 1}
        </span>{nom}
      </li>
    ))}
  </ol>
);

const Entreprise = ({ onSuivant }) => {
  const [reglages, setReglages] = useState(null);
  const [info, setInfo] = useState({});
  const [envoi, setEnvoi] = useState(false);
  useEffect(() => {
    api.get("/settings").then(({ data }) => { setReglages(data); setInfo(data.info_hote || {}); }).catch((e) => toast.error(errMsg(e)));
  }, []);
  const enregistrer = async () => {
    setEnvoi(true);
    try {
      await api.put("/settings", { info_hote: info, categories: reglages.categories, categories_revenus: reglages.categories_revenus });
      onSuivant();
    } catch (e) { toast.error(errMsg(e)); } finally { setEnvoi(false); }
  };
  if (!reglages) return <Loader2 className="h-6 w-6 animate-spin text-emerald-700" />;
  return (
    <div className="space-y-4" data-testid="configuration-entreprise">
      <p className="text-sm text-slate-600">Ces informations apparaissent dans vos rapports et vos lettres. Vous pourrez les modifier plus tard dans Paramètres.</p>
      <div className="grid gap-4 sm:grid-cols-2">
        {HOTE_FIELDS.map(([k, l]) => (
          <div key={k} className={`space-y-1 ${k === "adresse" ? "sm:col-span-2" : ""}`}>
            <Label>{l}</Label>
            <Input value={info[k] || ""} onChange={(e) => setInfo({ ...info, [k]: e.target.value })} data-testid={`configuration-${k}`} />
          </div>
        ))}
      </div>
      <Button onClick={enregistrer} disabled={envoi || !(info.nom || "").trim()} className="bg-emerald-700 hover:bg-emerald-800" data-testid="configuration-entreprise-suivant">
        {envoi && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Enregistrer et continuer
      </Button>
    </div>
  );
};

const Plan = () => {
  const [plan, setPlan] = useState(null);
  useEffect(() => { api.get("/plan-comptable").then((r) => setPlan(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  return (
    <div className="space-y-4" data-testid="configuration-plan">
      <p className="text-sm text-slate-600">Importez le plan comptable de votre entreprise (Excel ou CSV : numéro et nom de chaque compte). Vous pouvez aussi passer cette étape et l'importer plus tard dans Commentaires opérations.</p>
      {plan ? <PlanComptableButton plan={plan} onImported={setPlan} /> : <Loader2 className="h-5 w-5 animate-spin text-emerald-700" />}
    </div>
  );
};

const Fin = ({ onTerminer, envoi }) => (
  <div className="space-y-4" data-testid="configuration-fin">
    <p className="text-sm text-slate-600">Votre espace est prêt. Prochaine étape : importez vos relevés bancaires et vos rapports Airbnb et Booking dans la page Imports.</p>
    <p className="text-sm text-slate-600">Pensez à faire une sauvegarde régulière (Paramètres → Sauvegarde) et à la copier sur une clé USB ou un disque externe.</p>
    <Button onClick={onTerminer} disabled={envoi} className="bg-emerald-700 hover:bg-emerald-800" data-testid="configuration-terminer">
      {envoi && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Ouvrir l'application
    </Button>
  </div>
);

export default function Configuration() {
  const { rafraichirEtat } = useAuth();
  const navigate = useNavigate();
  const [etape, setEtape] = useState(0);
  const [envoi, setEnvoi] = useState(false);
  const terminer = async () => {
    setEnvoi(true);
    try { await api.post("/local/terminer"); await rafraichirEtat(); navigate("/"); }
    catch (e) { toast.error(errMsg(e)); } finally { setEnvoi(false); }
  };
  return (
    <div className="min-h-screen bg-slate-100 lg:grid lg:grid-cols-[280px_1fr]" data-testid="configuration-page">
      <aside className="bg-slate-950 p-6 lg:min-h-screen lg:p-10">
        <p className="mb-8 text-[11px] font-mono uppercase tracking-[0.25em] text-emerald-400">Configuration</p>
        <Etapes etape={etape} />
      </aside>
      <main className="mx-auto w-full max-w-5xl p-4 sm:p-8 lg:p-12">
        <h1 className="mb-6 text-3xl font-bold tracking-tight text-slate-900" data-testid="configuration-titre">{ETAPES[etape]}</h1>
        {etape === 0 && <Entreprise onSuivant={() => setEtape(1)} />}
        {etape === 1 && <LogementsPanel />}
        {etape === 2 && <Plan />}
        {etape === 3 && <Fin onTerminer={terminer} envoi={envoi} />}
        {(etape === 1 || etape === 2) && (
          <div className="mt-8 flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setEtape(etape - 1)} data-testid="configuration-retour">Retour</Button>
            <Button onClick={() => setEtape(etape + 1)} className="bg-emerald-700 hover:bg-emerald-800" data-testid="configuration-suivant">
              {etape === 2 ? "Continuer (ou passer)" : "Continuer"}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
